import React, { useEffect, useState } from 'react';
import { collectionGroup, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db, isSuperAdmin } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { storedTeacherPlan, TEACHER_PLANS, TeacherPlanId } from '../../config/teacherPlans';
import Button from '../../components/ui/Button';

interface TeacherRow {
  path: string;
  name: string;
  email: string;
  schoolId: string;
  role: string;
  plan: TeacherPlanId | null;
}

const TeacherPlanManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [rows, setRows] = useState<TeacherRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, TeacherPlanId>>({});
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isSuperAdmin(currentUser)) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const snapshot = await getDocs(collectionGroup(db, 'teachers'));
        const next = snapshot.docs
          .map((teacherDoc) => {
            const data = teacherDoc.data();
            const role = String(data.role || 'teacher');
            const locked = role === 'schoolAdmin' || role === 'superAdmin';
            return {
              path: teacherDoc.ref.path,
              name: String(data.name || '이름 없음'),
              email: String(data.email || ''),
              schoolId: String(data.schoolId || ''),
              role,
              plan: locked ? 'school' : storedTeacherPlan(data.plan),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        setRows(next);
        setDrafts(Object.fromEntries(next.map((row) => [row.path, row.plan || 'school'])));
      } catch (loadError) {
        console.error(loadError);
        setError('교사 요금제를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const savePlan = async (row: TeacherRow) => {
    const nextPlan = drafts[row.path];
    if (!nextPlan || nextPlan === row.plan) return;
    setMessage('');
    setError('');
    try {
      await updateDoc(doc(db, row.path), { plan: nextPlan });
      setRows((prev) => prev.map((item) => (item.path === row.path ? { ...item, plan: nextPlan } : item)));
      setMessage(`${row.name} 선생님의 요금제를 ${TEACHER_PLANS.find((plan) => plan.id === nextPlan)?.name}으로 바꿨습니다.`);
    } catch (saveError) {
      console.error(saveError);
      setError('요금제를 저장하지 못했습니다.');
    }
  };

  if (!isSuperAdmin(currentUser)) {
    return (
      <div style={{ fontFamily: 'var(--font-family-primary)', color: 'var(--Color-text-strong)' }}>
        최고 관리자만 교사 요금제를 바꿀 수 있습니다.
      </div>
    );
  }

  const visibleRows = rows.filter((row) => {
    const text = `${row.name} ${row.email} ${row.schoolId}`.toLowerCase();
    return text.includes(keyword.trim().toLowerCase());
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: 'var(--font-family-primary)' }}>
      <div>
        <div style={{ fontSize: '28px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>계정 등급</div>
        <div style={{ marginTop: '8px', fontSize: '14px', lineHeight: '21px', color: 'var(--Color-text-normal)' }}>
          교사 계정마다 무료, 학급, 학교 등급이 문서와 로그인 권한에 함께 저장됩니다. 등급이 비어 있던 기존 계정은 학교 등급으로 적어 두었습니다. 새로 승인되는 교사는 무료입니다.
        </div>
      </div>
      <input
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder="이름, 이메일, 학교 ID"
        aria-label="교사 검색"
        style={{
          width: '100%',
          maxWidth: '360px',
          height: '44px',
          borderRadius: '10px',
          border: '1px solid var(--Color-line-normal)',
          padding: '0 12px',
          fontFamily: 'inherit',
        }}
      />
      {message && <div style={{ color: 'var(--Color-text-strong)' }}>{message}</div>}
      {error && <div style={{ color: '#C2410C' }}>{error}</div>}
      {loading ? (
        <div>교사 목록을 불러오는 중입니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visibleRows.map((row) => {
            const locked = row.role === 'schoolAdmin' || row.role === 'superAdmin';
            return (
              <div
                key={row.path}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  borderRadius: '16px',
                  background: 'var(--Color-background-normal)',
                  border: '1px solid var(--Color-line-normal)',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                    {row.name}{row.plan ? '' : ' · 기존 계정(학교 요금제 적용 중)'}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                    {row.email} · {row.schoolId || '학교 없음'}
                    {locked ? ' · 관리자 계정은 학교 요금제입니다' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    aria-label={`${row.name} 요금제`}
                    value={locked ? 'school' : (drafts[row.path] || 'school')}
                    disabled={locked}
                    onChange={(event) => setDrafts((prev) => ({ ...prev, [row.path]: event.target.value as TeacherPlanId }))}
                    style={{ height: '40px', borderRadius: '8px', padding: '0 8px', fontFamily: 'inherit' }}
                  >
                    {TEACHER_PLANS.map((plan) => (
                      <option key={plan.id} value={plan.id}>{plan.name}</option>
                    ))}
                  </select>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => savePlan(row)}
                    disabled={locked || drafts[row.path] === row.plan}
                  >
                    저장
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeacherPlanManagement;
